import React, { useEffect, useState } from 'react';
import { fetchLights, fetchGroups, turnOnGroup, turnOffGroup, getAuthorizationCode, getAccessToken, refreshAccessToken, fetchRooms, getStoredToken } from '../models/HUEService';
import { useLocation } from 'react-router-dom';
import PageShell, { Btn, Pill } from '../components/design/PageShell';
import { Ic } from '../components/design/Icons';

const HUE = () => {
  const [lights, setLights] = useState([]);
  const [groups, setGroups] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(true);
  const location = useLocation();

  const fetchData = async (accessToken, username) => {
    try {
      const [ld, gd, rd] = await Promise.all([
        fetchLights(accessToken, username),
        fetchGroups(accessToken, username),
        fetchRooms(accessToken, username),
      ]);
      setLights(ld.data || []);
      setGroups(gd.data || []);
      setRooms(rd.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const handle = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const token = await getStoredToken();
      if (code) {
        await getAccessToken(code);
        window.history.replaceState({}, document.title, '/hue');
        if (token) await fetchData(token.accessToken, token.username);
      } else if (token) {
        await fetchData(token.accessToken, token.username);
      } else {
        try {
          await refreshAccessToken();
          const t2 = await getStoredToken();
          if (t2) await fetchData(t2.accessToken, t2.username);
          else setAuthorized(false);
        } catch (e) {
          setAuthorized(false);
          setLoading(false);
        }
      }
    };
    handle();
  }, [location]);

  const handleToggle = async (room, group) => {
    const isOn = group.on?.on;
    try {
      if (isOn) await turnOffGroup(group.id);
      else await turnOnGroup(group.id, { hue: 50000, sat: 200, bri: 254 });
      setGroups(prev => prev.map(g => g.id === group.id ? { ...g, on: { ...g.on, on: !isOn } } : g));
    } catch (e) { console.error(e); }
  };

  return (
    <PageShell
      section="hue"
      number="09.B"
      title="Philips HUE"
      subtitle="Éclairage connecté · bridge local"
      actions={!authorized && <Btn kind="primary" onClick={getAuthorizationCode}>Autoriser</Btn>}
    >
      {loading && <div className="mono" style={{ color: 'var(--ink-3)' }}>Chargement…</div>}

      {!loading && !authorized && (
        <div style={{ padding: 32, border: '1px solid var(--warn)', background: 'color-mix(in oklch, var(--warn), transparent 90%)' }}>
          <div style={{ fontSize: 16, fontWeight: 500 }}>Autorisation requise</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8, letterSpacing: '0.08em' }}>
            Cliquez sur « Autoriser » pour connecter votre bridge HUE.
          </div>
        </div>
      )}

      {!loading && authorized && (
        <>
          <section className="ta-stats" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div>
              <div className="eyebrow">{"// "}Pièces</div>
              <div className="f">{rooms.length}</div>
              <div className="d">configurées</div>
            </div>
            <div>
              <div className="eyebrow">{"// "}Ampoules</div>
              <div className="f">{lights.length}</div>
              <div className="d">connectées</div>
            </div>
            <div>
              <div className="eyebrow">{"// "}Allumées</div>
              <div className="f" style={{ color: 'var(--accent)' }}>
                {groups.filter(g => g.on?.on).length}
              </div>
              <div className="d">actives</div>
            </div>
          </section>

          <div className="eyebrow" style={{ marginBottom: 20 }}>§ Pièces</div>
          <section style={{ borderTop: '1px solid var(--rule)' }}>
            {rooms.map(room => {
              const group = groups.find(g => g.id === room.services?.[0]?.rid);
              if (!group) return null;
              const on = group.on?.on;
              return (
                <button
                  key={room.id}
                  onClick={() => handleToggle(room, group)}
                  style={{
                    display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: 24, alignItems: 'center',
                    width: '100%', padding: '18px 0',
                    borderBottom: '1px solid var(--rule)',
                    background: 'transparent', border: '0 0 1px 0 solid var(--rule)',
                    textAlign: 'left', cursor: 'pointer', color: 'inherit',
                  }}
                >
                  <span style={{ color: on ? 'var(--accent)' : 'var(--ink-4)', display: 'grid', placeItems: 'center' }}>
                    {Ic.bulb}
                  </span>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 500 }}>{room.metadata?.name}</div>
                    <div className="mono" style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 4, letterSpacing: '0.08em' }}>
                      ID · {String(room.id).slice(0, 8)}
                    </div>
                  </div>
                  <Pill tone={on ? 'accent' : 'muted'}>{on ? 'allumé' : 'éteint'}</Pill>
                </button>
              );
            })}
          </section>
        </>
      )}
    </PageShell>
  );
};

export default HUE;
